// Punto único de acceso a los datos. Si algún día llega un GTFS oficial (NAP del Ministerio),
// solo debe cambiar este módulo: la app y el planificador siguen viendo los mismos tipos.
//
// Los GeoJSON usan la convención estándar [lon, lat]; internamente convertimos a LatLng
// { lat, lon } para que el resto del código no se confunda.

import type { LatLng } from "./geo";

export type LineId = string;

export interface BusLine {
  id: LineId;
  name: string;
  color: string;
  group: LineGroup;
  paths: LatLng[][]; // MultiLineString
}

export type LineGroup = "urbana" | "cementerio" | "nocturna" | "pedania";

export interface BusStop {
  idx: number; // índice estable en el fichero, lo usan los ejes de `edges.json`
  id: number; // id de la parada en la API del operador (= número del poste)
  number: string; // número visible en el poste de la parada
  name: string;
  lines: LineId[];
  ll: LatLng;
}

export interface Arrow {
  line: LineId;
  ll: LatLng;
  bearing: number; // grados horarios desde el norte
}

// Trayecto oficial (una variante de ida o vuelta) con su trazado por calles. Cada parada cae
// exactamente en un vértice de `path` (`stopVertex`).
export interface BusRoute {
  id: number;
  line: LineId;
  sense: string;
  destination: string;
  stops: { id: number; dist: number }[]; // dist = metros oficiales desde el inicio
  stopVertex: number[];
  path: LatLng[];
}

export type Edge = readonly [number, number, number]; // [idxA, idxB, metros]
export type EdgeMap = Readonly<Record<LineId, readonly Edge[]>>;

export interface Dataset {
  lines: readonly BusLine[];
  stops: readonly BusStop[];
  arrows: readonly Arrow[];
  edges: EdgeMap;
  routes: ReadonlyMap<LineId, readonly BusRoute[]>;
  // Índices auxiliares que hacen O(1) el acceso más frecuente del planificador y de la UI.
  linesById: ReadonlyMap<LineId, BusLine>;
  stopsByIdx: readonly BusStop[]; // stopsByIdx[i] === stops[i] tras validar orden
  stopsById: ReadonlyMap<number, BusStop>;
}

// --- Tipos GeoJSON estrictos: no usamos `any` en runtime. ---
interface FCLine {
  type: "FeatureCollection";
  features: {
    type: "Feature";
    properties: { id: string; name: string; color: string; group?: LineGroup };
    geometry: { type: "MultiLineString"; coordinates: number[][][] };
  }[];
}
interface FCStop {
  type: "FeatureCollection";
  features: {
    type: "Feature";
    properties: { idx: number; id: number; number: string; name: string; lines: string[] };
    geometry: { type: "Point"; coordinates: number[] };
  }[];
}
interface FCArrow {
  type: "FeatureCollection";
  features: {
    type: "Feature";
    properties: { line: string; bearing: number };
    geometry: { type: "Point"; coordinates: number[] };
  }[];
}
type EdgeJson = Record<string, [number, number, number][]>;
interface RoutesJson {
  lines: Record<
    string,
    {
      id: number;
      sense: string;
      destination: string;
      stops: [number, number][];
      stopVertex: number[];
      coords: number[][];
    }[]
  >;
}

const toLL = (coord: readonly number[]): LatLng => ({ lon: coord[0]!, lat: coord[1]! });

function parseLines(fc: FCLine): BusLine[] {
  return fc.features.map((f) => ({
    id: f.properties.id,
    name: f.properties.name,
    color: f.properties.color,
    group: f.properties.group ?? "urbana",
    paths: f.geometry.coordinates.map((ring) => ring.map(toLL)),
  }));
}

function parseStops(fc: FCStop): BusStop[] {
  return fc.features.map((f) => ({
    idx: f.properties.idx,
    id: f.properties.id,
    number: f.properties.number,
    name: f.properties.name,
    lines: f.properties.lines.slice(),
    ll: toLL(f.geometry.coordinates),
  }));
}

function parseArrows(fc: FCArrow): Arrow[] {
  return fc.features.map((f) => ({
    line: f.properties.line,
    ll: toLL(f.geometry.coordinates),
    bearing: f.properties.bearing,
  }));
}

// Construcción del dataset a partir de los 4 JSON ya cargados. Se separa del fetch para poder
// llamarla también en los tests con datos precargados por el disco.
// `hide`: grupos de líneas que se quitan del todo (líneas, trayectos, tramos y flechas; las
// paradas se quedan, para no mover los índices, pero sin esas líneas).
export function buildDataset(
  raw: {
    lines: FCLine;
    stops: FCStop;
    arrows: FCArrow;
    edges: EdgeJson;
    routes: RoutesJson;
  },
  hide: ReadonlySet<string> = new Set(),
): Dataset {
  const allLines = parseLines(raw.lines);
  const hidden = new Set(allLines.filter((l) => hide.has(l.group)).map((l) => l.id));
  const lines = allLines.filter((l) => !hidden.has(l.id));
  const stops = parseStops(raw.stops).map((s) => ({ ...s, lines: s.lines.filter((l) => !hidden.has(l)) }));
  const arrows = parseArrows(raw.arrows).filter((a) => !hidden.has(a.line));

  const linesById = new Map<LineId, BusLine>(lines.map((l) => [l.id, l]));

  // Los tests comprueban esta invariante, pero merece un chequeo temprano en runtime también:
  // si el índice no está bien alineado, todo lo demás (edges.json) es basura.
  const stopsByIdx: BusStop[] = new Array(stops.length);
  for (const s of stops) {
    if (s.idx < 0 || s.idx >= stops.length) {
      throw new Error(`stop.idx fuera de rango: ${s.idx}`);
    }
    if (stopsByIdx[s.idx] !== undefined) {
      throw new Error(`stop.idx duplicado: ${s.idx}`);
    }
    stopsByIdx[s.idx] = s;
  }

  // Freezing superficial. Interior sigue mutable en TS solo por Convención.
  const edges: EdgeMap = Object.freeze(
    Object.fromEntries(
      Object.entries(raw.edges).filter(([lineId]) => !hidden.has(lineId)).map(([lineId, arr]) => [
        lineId,
        arr.map(([a, b, m]) => [a, b, m] as const),
      ]),
    ),
  );

  const routes = new Map<LineId, BusRoute[]>();
  for (const [line, list] of Object.entries(raw.routes.lines)) {
    if (hidden.has(line)) continue;
    routes.set(
      line,
      list.map((r) => ({
        id: r.id,
        line,
        sense: r.sense,
        destination: r.destination,
        stops: r.stops.map(([id, dist]) => ({ id, dist })),
        stopVertex: r.stopVertex.slice(),
        path: r.coords.map(toLL),
      })),
    );
  }

  return {
    lines,
    stops,
    arrows,
    edges,
    routes,
    linesById,
    stopsByIdx,
    stopsById: new Map(stops.map((st) => [st.id, st])),
  };
}

// Fetch de los 4 archivos y construcción del dataset. En build se sirven bajo `/data/` desde
// el bundle estático (public/data/ si se copia allí; ver copyToPublic más abajo).
export async function loadDataset(baseUrl = "/data", hide: ReadonlySet<string> = new Set()): Promise<Dataset> {
  const [lines, stops, arrows, edges, routes] = await Promise.all([
    fetchJson<FCLine>(`${baseUrl}/lines.geojson`),
    fetchJson<FCStop>(`${baseUrl}/stops.geojson`),
    fetchJson<FCArrow>(`${baseUrl}/arrows.geojson`),
    fetchJson<EdgeJson>(`${baseUrl}/edges.json`),
    fetchJson<RoutesJson>(`${baseUrl}/routes.json`),
  ]);
  return buildDataset({ lines, stops, arrows, edges, routes }, hide);
}

async function fetchJson<T>(url: string): Promise<T> {
  const r = await fetch(url, { cache: "force-cache" });
  if (!r.ok) throw new Error(`No pude cargar ${url}: HTTP ${r.status}`);
  return (await r.json()) as T;
}

// Trazado por calles de la línea entre dos paradas, en el sentido en que el bus las recorre.
// Busca un trayecto que pase por la subida y después por la bajada; null si no hay ninguno.
export function pathBetweenStops(
  ds: Dataset,
  line: LineId,
  boardIdx: number,
  alightIdx: number,
): LatLng[] | null {
  const a = ds.stopsByIdx[boardIdx]?.id;
  const b = ds.stopsByIdx[alightIdx]?.id;
  if (a === undefined || b === undefined) return null;
  let best: LatLng[] | null = null;
  for (const r of ds.routes.get(line) ?? []) {
    const i = r.stops.findIndex((s) => s.id === a);
    if (i < 0) continue;
    const j = r.stops.findIndex((s, k) => k > i && s.id === b);
    if (j < 0) continue;
    const path = r.path.slice(r.stopVertex[i], r.stopVertex[j]! + 1);
    if (!best || path.length < best.length) best = path;
  }
  return best;
}

// Sentido de marcha en cada parada, según los trayectos que paran en ella (el tramo del trazado
// alrededor de su vértice). `null` si unos van en un sentido y otros en el contrario.
export function stopHeadings(ds: Dataset): Map<number, { x: number; y: number } | null> {
  const out = new Map<number, { x: number; y: number } | null>();
  for (const routes of ds.routes.values()) {
    for (const r of routes) {
      r.stops.forEach((st, k) => {
        const stop = ds.stopsById.get(st.id);
        const v = r.stopVertex[k];
        if (!stop || v === undefined) return;
        const a = r.path[Math.max(0, v - 1)]!;
        const b = r.path[Math.min(r.path.length - 1, v + 1)]!;
        const x = (b.lon - a.lon) * Math.cos((a.lat * Math.PI) / 180);
        const y = b.lat - a.lat;
        const l = Math.hypot(x, y);
        if (l === 0) return;
        const h = { x: x / l, y: y / l };
        if (!out.has(stop.idx)) out.set(stop.idx, h);
        const cur = out.get(stop.idx);
        if (cur && cur.x * h.x + cur.y * h.y < 0) out.set(stop.idx, null);
      });
    }
  }
  return out;
}
